<template>
  <div class="handwriting-info-container">
    <!-- 文件資訊摘要卡片 -->
    <el-card v-if="docInfo" class="doc-info-card" shadow="never">
      <div class="doc-info-content">
        <i class="el-icon-document doc-icon" />
        <div class="doc-info-text">
          <span class="doc-info-item">
            <span class="doc-info-label">文件號碼：</span>
            <span class="doc-info-value">{{ docInfo.DOC_NO }}</span>
          </span>
          <span class="doc-info-divider">|</span>
          <span class="doc-info-item">
            <span class="doc-info-label">文件名稱：</span>
            <span class="doc-info-value">{{ docInfo.DOC_NAME }}</span>
          </span>
        </div>
      </div>
    </el-card>

    <!-- 簽名者卡片列表 -->
    <div v-loading="listLoading" class="signature-cards">
      <el-card v-for="(item, index) in signdataList" :key="item.id" class="signature-card" shadow="never">
        <!-- 卡片標題 -->
        <div slot="header" class="card-header">
          <span class="signer-title">
            <i class="el-icon-user" />
            簽名 {{ index + 1 }}：{{ item.SIGNER_NAME | signerNameFilter }}
          </span>
          <span v-if="item.SIGN_DATE" class="sign-date">
            <i class="el-icon-time" />
            {{ item.SIGN_DATE }}
          </span>
        </div>

        <!-- 卡片主體：左側簽名影像 + 右側資訊 -->
        <el-row :gutter="24">
          <!-- 左側：簽名影像 -->
          <el-col :xs="24" :sm="24" :md="8" :lg="6" class="signature-image-col">
            <div class="signature-image-wrapper">
              <el-image
                :src="item.extra.signedPhoto"
                :preview-src-list="[item.extra.signedPhoto]"
                fit="contain"
                class="signature-image">
                <div slot="placeholder" class="image-placeholder">
                  <i class="el-icon-loading" />
                </div>
                <div slot="error" class="image-error">
                  <i class="el-icon-picture-outline" />
                  <span>載入失敗</span>
                </div>
              </el-image>
              <div class="image-hint">
                <i class="el-icon-zoom-in" />
                點擊放大
              </div>
            </div>
          </el-col>

          <!-- 右側：筆跡參數 -->
          <el-col :xs="24" :sm="24" :md="16" :lg="18" class="params-col">
            <!-- 摘要資訊 -->
            <div class="summary-section">
              <div class="summary-item">
                <span class="summary-label">總時間</span>
                <span class="summary-value">{{ item.extra.totalTime }} ms</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">時間差</span>
                <span class="summary-value">{{ item.extra.timeInterval }} ms</span>
              </div>
            </div>

            <!-- 參數分組 Tabs -->
            <el-tabs v-model="item.activeTab" class="params-tabs">
              <!-- 位置參數 -->
              <el-tab-pane label="位置" name="position">
                <div class="params-grid">
                  <div class="param-item position-theme">
                    <div class="param-header">
                      <span class="param-label">X 軸位移</span>
                      <span class="param-value">{{ item.extra.xAxis }}</span>
                    </div>
                    <el-progress
                      :percentage="getPercentage(item.extra.xAxis, 'position')"
                      :stroke-width="8"
                      :show-text="false"
                      color="#5b9bd5" />
                  </div>
                  <div class="param-item position-theme">
                    <div class="param-header">
                      <span class="param-label">Y 軸位移</span>
                      <span class="param-value">{{ item.extra.yAxis }}</span>
                    </div>
                    <el-progress
                      :percentage="getPercentage(item.extra.yAxis, 'position')"
                      :stroke-width="8"
                      :show-text="false"
                      color="#5b9bd5" />
                  </div>
                  <div class="param-item position-theme">
                    <div class="param-header">
                      <span class="param-label">總時間</span>
                      <span class="param-value">{{ item.extra.totalTime }} ms</span>
                    </div>
                    <el-progress
                      :percentage="getPercentage(item.extra.totalTime, 'time')"
                      :stroke-width="8"
                      :show-text="false"
                      color="#5b9bd5" />
                  </div>
                  <div class="param-item position-theme">
                    <div class="param-header">
                      <span class="param-label">時間差</span>
                      <span class="param-value">{{ item.extra.timeInterval }} ms</span>
                    </div>
                    <el-progress
                      :percentage="getPercentage(item.extra.timeInterval, 'timeInterval')"
                      :stroke-width="8"
                      :show-text="false"
                      color="#5b9bd5" />
                  </div>
                </div>
              </el-tab-pane>

              <!-- 速度參數 -->
              <el-tab-pane label="速度" name="speed">
                <div class="params-grid">
                  <div class="param-item speed-theme">
                    <div class="param-header">
                      <span class="param-label">X 軸速度</span>
                      <span class="param-value">{{ item.extra.xSpeed }} px/ms</span>
                    </div>
                    <el-progress
                      :percentage="getPercentage(item.extra.xSpeed, 'speed')"
                      :stroke-width="8"
                      :show-text="false"
                      color="#42b983" />
                  </div>
                  <div class="param-item speed-theme">
                    <div class="param-header">
                      <span class="param-label">Y 軸速度</span>
                      <span class="param-value">{{ item.extra.ySpeed }} px/ms</span>
                    </div>
                    <el-progress
                      :percentage="getPercentage(item.extra.ySpeed, 'speed')"
                      :stroke-width="8"
                      :show-text="false"
                      color="#42b983" />
                  </div>
                </div>
              </el-tab-pane>

              <!-- 加速度參數 -->
              <el-tab-pane label="加速度" name="acceleration">
                <div class="params-grid">
                  <div class="param-item acceleration-theme">
                    <div class="param-header">
                      <span class="param-label">X 軸加速度</span>
                      <span class="param-value">{{ item.extra.xAcceleration }} px/ms²</span>
                    </div>
                    <el-progress
                      :percentage="getPercentage(item.extra.xAcceleration, 'acceleration')"
                      :stroke-width="8"
                      :show-text="false"
                      color="#E6A23C" />
                  </div>
                  <div class="param-item acceleration-theme">
                    <div class="param-header">
                      <span class="param-label">Y 軸加速度</span>
                      <span class="param-value">{{ item.extra.yAcceleration }} px/ms²</span>
                    </div>
                    <el-progress
                      :percentage="getPercentage(item.extra.yAcceleration, 'acceleration')"
                      :stroke-width="8"
                      :show-text="false"
                      color="#E6A23C" />
                  </div>
                </div>
              </el-tab-pane>

              <!-- 傾角參數 -->
              <el-tab-pane label="傾角" name="inclination">
                <div class="params-grid">
                  <div class="param-item inclination-theme">
                    <div class="param-header">
                      <span class="param-label">X 軸傾角</span>
                      <span class="param-value">{{ item.extra.xInclination }} rad</span>
                    </div>
                    <el-progress
                      :percentage="getPercentage(item.extra.xInclination, 'inclination')"
                      :stroke-width="8"
                      :show-text="false"
                      color="#909399" />
                  </div>
                  <div class="param-item inclination-theme">
                    <div class="param-header">
                      <span class="param-label">Y 軸傾角</span>
                      <span class="param-value">{{ item.extra.yInclination }} rad</span>
                    </div>
                    <el-progress
                      :percentage="getPercentage(item.extra.yInclination, 'inclination')"
                      :stroke-width="8"
                      :show-text="false"
                      color="#909399" />
                  </div>
                  <div class="param-item inclination-theme">
                    <div class="param-header">
                      <span class="param-label">筆方位角</span>
                      <span class="param-value">{{ item.extra.penAzimuth }} rad</span>
                    </div>
                    <el-progress
                      :percentage="getPercentage(item.extra.penAzimuth, 'angle')"
                      :stroke-width="8"
                      :show-text="false"
                      color="#909399" />
                  </div>
                  <div class="param-item inclination-theme">
                    <div class="param-header">
                      <span class="param-label">筆仰角</span>
                      <span class="param-value">{{ item.extra.penElevation }} px</span>
                    </div>
                    <el-progress
                      :percentage="getPercentage(item.extra.penElevation, 'elevation')"
                      :stroke-width="8"
                      :show-text="false"
                      color="#909399" />
                  </div>
                </div>
              </el-tab-pane>
            </el-tabs>
          </el-col>
        </el-row>
      </el-card>

      <!-- 無資料提示 -->
      <el-empty v-if="!listLoading && signdataList.length === 0" description="暫無簽名資料" />
    </div>
  </div>
</template>

<script>
export default {
  name: 'HandwritingInfo',
  filters: {
    signerNameFilter(value) {
      const signerNameMap = {
        'Main Signer': '本人',
        Doctor: '醫師',
        Witness1: '見證人1',
        Witness2: '見證人2'
      }

      return signerNameMap[value]
    }
  },
  props: {
    data: {
      type: Object,
      default: undefined
    }
  },
  data() {
    return {
      signdataList: [],
      docInfo: null,
      total: 0,
      listLoading: false,
      // 用於計算進度條百分比的參考值
      maxValues: {
        position: 100,
        time: 5000,
        timeInterval: 100,
        speed: 2,
        acceleration: 0.5,
        inclination: 2,
        angle: 2,
        elevation: 200
      }
    }
  },
  activated: function () {
    this.getSignDataList()
  },
  created() {
    this.getSignDataList()
  },
  methods: {
    // 計算進度條百分比
    getPercentage(value, type) {
      const numValue = Math.abs(parseFloat(value) || 0)
      const maxValue = this.maxValues[type] || 100
      const percentage = (numValue / maxValue) * 100
      return Math.min(Math.max(percentage, 0), 100)
    },

    getSignDataList() {
      if (this.data === undefined) return
      this.listLoading = true

      // 設置文件資訊
      this.docInfo = {
        DOC_NO: this.data.DOC_NO,
        DOC_NAME: this.data.DOC_NAME
      }

      this.signdataList = []

      const formdata = JSON.parse(this.data.SIGN_DATA)
      const signdata = formdata.filter((x) => x.type === 'signature')

      // parsing biometric data
      for (let j = 0; j < signdata.length; j++) {
        if (signdata[j].extra.signedPhoto === undefined) continue

        signdata[j].SIGNER_NAME = undefined
        const usertype = signdata[j].extra.usertype
        let relation
        if (usertype === 'Main Signer') {
          relation = formdata.filter((x) => x.extra.usertype === usertype && x.extra.type === 'relationshiptopatient')
          if (relation.length > 0) {
            if (relation[0].extra.value !== '本人') {
              signdata[j].SIGNER_NAME = '病患之' + relation[0].extra.relationship + ' ' + relation[0].extra.signername
            }
          }
          if (signdata[j].SIGNER_NAME === undefined) {
            signdata[j].SIGNER_NAME = usertype
          }
        } else {
          relation = formdata.filter((x) => x.extra.usertype === usertype && x.extra.type === 'relationshiptopatient')
          if (relation.length > 0) {
            if (relation[0].extra.value !== '本人') {
              signdata[j].SIGNER_NAME = '病患之' + relation[0].extra.relationship + ' ' + relation[0].extra.signername
            }
          }
          if (signdata[j].SIGNER_NAME === undefined) {
            const namedata = formdata.filter((x) => x.extra.usertype === usertype && x.extra.fieldname === 'Name')
            if (namedata.length > 0 && namedata[0].extra.value !== undefined && namedata[0].extra.value !== '') {
              signdata[j].SIGNER_NAME = usertype + ' ' + namedata[0].extra.value
            }
          }
          if (signdata[j].SIGNER_NAME === undefined) {
            signdata[j].SIGNER_NAME = usertype
          }
        }

        const signdate = formdata.filter(
          (x) => x.extra.usertype === usertype && x.extra.fieldname === 'DateofSignature'
        )
        if (signdate.length > 0) {
          signdata[j].SIGN_DATE = signdate[0].extra.value
        }

        signdata[j].DOC_NO = this.data.DOC_NO
        signdata[j].DOC_NAME = this.data.DOC_NAME
        signdata[j].extra.xAxis = 0
        signdata[j].extra.yAxis = 0
        signdata[j].extra.totalTime = 0
        signdata[j].extra.timeInterval = 0
        signdata[j].extra.xSpeed = 0
        signdata[j].extra.ySpeed = 0
        signdata[j].extra.xAcceleration = 0
        signdata[j].extra.yAcceleration = 0
        signdata[j].extra.xInclination = 0
        signdata[j].extra.yInclination = 0
        signdata[j].extra.penAzimuth = 0
        signdata[j].extra.penElevation = 0
        // 新增 activeTab 用於 Tabs 切換
        signdata[j].activeTab = 'position'
        let yMin = 0
        let yMax = 0

        for (let k = 0; k < signdata[j].extra.dataPoint.length; k++) {
          const points = signdata[j].extra.dataPoint[k].points
          let xAxis = 0
          let yAxis = 0
          let timeInterval = 0
          let xSpeed = 0
          let ySpeed = 0
          let xAcceleration = 0
          let yAcceleration = 0
          let xInclination = 0
          let yInclination = 0
          let penAzimuth = 0

          if (points.length > 0) {
            penAzimuth += points[0].y / points[0].x
            if (yMin > points[0].y) yMin = points[0].y
            if (yMax < points[0].y) yMax = points[0].y
          }
          for (let t = 1; t < points.length; t++) {
            xAxis += points[t].x - points[t - 1].x
            yAxis += points[t].y - points[t - 1].y
            timeInterval += points[t].time - points[t - 1].time
            xSpeed += (points[t].x - points[t - 1].x) / (points[t].time - points[t - 1].time)
            ySpeed += (points[t].y - points[t - 1].y) / (points[t].time - points[t - 1].time)
            xInclination += Math.atan((points[t].y - points[t - 1].y) / (points[t].x - points[t - 1].x))
            yInclination += Math.atan((points[t].x - points[t - 1].x) / (points[t].y - points[t - 1].y))
            penAzimuth += Math.atan(points[t].y / points[t].x)
            if (yMin > points[t].y) yMin = points[t].y
            if (yMax < points[t].y) yMax = points[t].y

            if (t > 1) {
              xAcceleration +=
                (points[t].x - points[t - 1].x) / (points[t].time - points[t - 1].time) -
                (points[t - 1].x - points[t - 2].x) / (points[t - 1].time - points[t - 2].time)
              yAcceleration +=
                (points[t].y - points[t - 1].y) / (points[t].time - points[t - 1].time) -
                (points[t - 1].y - points[t - 2].y) / (points[t - 1].time - points[t - 2].time)
            }
          }
          signdata[j].extra.xAxis += xAxis / (points.length - 1)
          signdata[j].extra.yAxis += yAxis / (points.length - 1)
          signdata[j].extra.timeInterval += timeInterval / (points.length - 1)
          signdata[j].extra.xSpeed += xSpeed / (points.length - 1)
          signdata[j].extra.ySpeed += ySpeed / (points.length - 1)
          signdata[j].extra.xAcceleration += xAcceleration / (points.length - 2)
          signdata[j].extra.yAcceleration += yAcceleration / (points.length - 2)
          signdata[j].extra.xInclination += xInclination / (points.length - 1)
          signdata[j].extra.yInclination += yInclination / (points.length - 1)
          signdata[j].extra.penAzimuth += penAzimuth / points.length
        }
        signdata[j].extra.xAxis = parseFloat(signdata[j].extra.xAxis / signdata[j].extra.dataPoint.length).toFixed(4)
        signdata[j].extra.yAxis = parseFloat(signdata[j].extra.yAxis / signdata[j].extra.dataPoint.length).toFixed(4)
        signdata[j].extra.timeInterval = parseFloat(
          signdata[j].extra.timeInterval / signdata[j].extra.dataPoint.length
        ).toFixed(4)
        signdata[j].extra.xSpeed = parseFloat(signdata[j].extra.xSpeed / signdata[j].extra.dataPoint.length).toFixed(4)
        signdata[j].extra.ySpeed = parseFloat(signdata[j].extra.ySpeed / signdata[j].extra.dataPoint.length).toFixed(4)
        signdata[j].extra.xAcceleration = parseFloat(
          signdata[j].extra.xAcceleration / signdata[j].extra.dataPoint.length
        ).toFixed(4)
        signdata[j].extra.yAcceleration = parseFloat(
          signdata[j].extra.yAcceleration / signdata[j].extra.dataPoint.length
        ).toFixed(4)
        signdata[j].extra.xInclination = parseFloat(
          signdata[j].extra.xInclination / signdata[j].extra.dataPoint.length
        ).toFixed(4)
        signdata[j].extra.yInclination = parseFloat(
          signdata[j].extra.yInclination / signdata[j].extra.dataPoint.length
        ).toFixed(4)
        signdata[j].extra.penAzimuth = parseFloat(
          signdata[j].extra.penAzimuth / signdata[j].extra.dataPoint.length
        ).toFixed(4)
        signdata[j].extra.penElevation = yMax - yMin

        const len = signdata[j].extra.dataPoint.length
        if (len > 0) {
          const plen = signdata[j].extra.dataPoint[len - 1].points.length
          signdata[j].extra.totalTime =
            signdata[j].extra.dataPoint[len - 1].points[plen - 1].time - signdata[j].extra.dataPoint[0].points[0].time
        } else signdata[j].extra.totalTime = 0

        signdata[j].id = this.signdataList.length + 1
        this.signdataList.push(signdata[j])
      }

      this.total = this.signdataList.length
      this.listLoading = false
    }
  }
}
</script>

<style lang="scss" scoped>
.handwriting-info-container {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100%;
}

// 文件資訊卡片
.doc-info-card {
  margin-bottom: 20px;
  border-radius: 8px;

  .doc-info-content {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .doc-icon {
    font-size: 32px;
    color: #42b983;
  }

  .doc-info-text {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
  }

  .doc-info-item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .doc-info-label {
    color: #909399;
    font-size: 14px;
  }

  .doc-info-value {
    color: #303133;
    font-size: 14px;
    font-weight: 500;
  }

  .doc-info-divider {
    color: #dcdfe6;
    margin: 0 8px;
  }
}

// 簽名卡片列表
.signature-cards {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

// 單個簽名卡片
.signature-card {
  border-radius: 12px;
  overflow: hidden;

  /deep/ .el-card__header {
    background: linear-gradient(135deg, #304156 0%, #1f2d3d 100%);
    padding: 16px 20px;
    border-bottom: none;
  }

  .card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
  }

  .signer-title {
    color: #fff;
    font-size: 16px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;

    i {
      font-size: 18px;
    }
  }

  .sign-date {
    color: rgba(255, 255, 255, 0.85);
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 4px;
  }
}

// 簽名影像區域
.signature-image-col {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;

  @media (min-width: 992px) {
    margin-bottom: 0;
  }
}

.signature-image-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 280px;
}

.signature-image {
  width: 100%;
  height: 160px;
  border: 2px solid #e4e7ed;
  border-radius: 8px;
  background-color: #fafafa;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: #42b983;
    box-shadow: 0 4px 12px rgba(66, 185, 131, 0.2);
  }

  /deep/ img {
    object-fit: contain;
  }
}

.image-placeholder,
.image-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #909399;
  font-size: 14px;

  i {
    font-size: 32px;
    margin-bottom: 8px;
  }
}

.image-hint {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
  display: flex;
  align-items: center;
  gap: 4px;

  i {
    font-size: 14px;
  }
}

// 參數區域
.params-col {
  display: flex;
  flex-direction: column;
}

// 摘要區域
.summary-section {
  display: flex;
  gap: 24px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e4e7ed 100%);
  border-radius: 8px;
}

.summary-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.summary-label {
  font-size: 12px;
  color: #909399;
}

.summary-value {
  font-size: 18px;
  font-weight: 600;
  color: #303133;
}

// Tabs 樣式
.params-tabs {
  /deep/ .el-tabs__header {
    margin-bottom: 16px;
  }

  /deep/ .el-tabs__item {
    font-size: 14px;
    font-weight: 500;

    &.is-active {
      font-weight: 600;
    }
  }

  /deep/ .el-tabs__nav-wrap::after {
    height: 1px;
  }
}

// 參數網格
.params-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
}

// 單個參數項
.param-item {
  padding: 12px 16px;
  background-color: #fafafa;
  border-radius: 8px;
  border-left: 3px solid transparent;
  transition: all 0.3s ease;

  // 位置主題 - 信息藍
  &.position-theme {
    border-left-color: #5b9bd5;
  }

  // 速度主題 - 主題綠
  &.speed-theme {
    border-left-color: #42b983;
  }

  // 加速度主題 - 橙色
  &.acceleration-theme {
    border-left-color: #e6a23c;
  }

  // 傾角主題 - 灰色
  &.inclination-theme {
    border-left-color: #909399;
  }
}

.param-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.param-label {
  font-size: 13px;
  color: #606266;
}

.param-value {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

// 進度條樣式調整
/deep/ .el-progress-bar__outer {
  background-color: #e9ecef;
  border-radius: 4px;
}

/deep/ .el-progress-bar__inner {
  border-radius: 4px;
}

// 響應式調整
@media (max-width: 767px) {
  .handwriting-info-container {
    padding: 12px;
  }

  .doc-info-card {
    .doc-info-content {
      flex-direction: column;
      align-items: flex-start;
    }

    .doc-info-text {
      flex-direction: column;
      align-items: flex-start;
    }

    .doc-info-divider {
      display: none;
    }
  }

  .summary-section {
    flex-direction: column;
    gap: 12px;
  }

  .params-grid {
    grid-template-columns: 1fr;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
